<template>
    <div v-if="isLoading" class="loading-wrapper">
        <img
            class="spinner"
            src="/apps/assets/img/ajax-spinner.gif"
            alt="Spinning loader"
        />
    </div>

    <ul v-else-if="results.length > 0">
        <li v-for="(result, index) in results" :key="index">
            <button
                data-testid="result-item"
                class="btn-option"
                @click="viewAppointment(result)"
            >
                <module-icon name="schedule" class="icon" />
                <span class="d-flex flex-column">
                    <span class="fw-medium">{{ result.label }}</span>
                    <span class="module">Schedule</span>
                </span>
            </button>
        </li>
    </ul>

    <p v-else-if="searchInput.trim() !== ''" class="no-data">
        No matches found for "<span class="fw-medium">{{ searchInput }}</span
        >"
    </p>
</template>

<script>
import ModuleIcon from "@/module/layout/module-icon";
import { debounce } from "lodash";

const APPOINTMENT_SEARCH_URI = `${process.env.BARRAMUNDI}schedule/appointment/search`;

export default {
    name: "TabPanelSchedule",
    components: { ModuleIcon },
    props: {
        searchInput: {
            type: String
        }
    },
    data() {
        return {
            isLoading: false,
            lastSearchedQuery: "",
            requestId: 0,
            results: []
        };
    },
    watch: {
        searchInput() {
            this.handleSearch();
        }
    },
    created() {
        this.debouncedSearch = debounce(this.fetchResult, 350);
    },
    mounted() {
        if (this.searchInput) {
            this.handleSearch();
        }
    },
    methods: {
        handleSearch() {
            const currentRequestId = ++this.requestId;

            const query = this.searchInput.trim();
            this.isLoading = query !== "";

            if (query === "") {
                this.results = [];
                return;
            }

            this.debouncedSearch(query, currentRequestId);
        },
        async fetchResult(query, currentRequestId) {
            const res = await this.$http.post(APPOINTMENT_SEARCH_URI, {
                query: query
            });

            const { message, resource } = res.data.response;

            if (message.code === 200) {
                if (currentRequestId !== this.requestId) {
                    return;
                }
                this.results = resource
                    .map(item => this.formatList(item))
                    .filter(
                        (item, index, arr) =>
                            index ===
                                arr.findIndex(obj => obj.id === item.id) &&
                            item.label
                                .toLowerCase()
                                .includes(query.toLowerCase())
                    );
                this.isLoading = false;
            } else {
                this.results = [];
                this.isLoading = false;
            }
        },
        formatList(item) {
            const {
                id,
                start: dateStart,
                reason,
                patient_name,
                client_firstname,
                client_lastname
            } = item;

            let dateLabel;
            let dateMoment;
            let momentRawDateFormat = "YYYY-MM-DD HH:mm";
            let timezone = this._timeZone;

            if (["UTC", "GMT"].includes(dateStart.timezone)) {
                dateMoment = moment
                    .utc(dateStart.date, momentRawDateFormat)
                    .tz(timezone);
            } else {
                dateMoment = moment(dateStart.date, momentRawDateFormat).tz(
                    timezone
                );
            }

            dateLabel = this._carbonDateTimeDisplay(dateStart);

            return {
                id,
                label: `${dateLabel} - ${reason} for ${patient_name} (${client_lastname}, ${client_firstname})`,
                date: dateStart,
                dateMoment
            };
        },
        viewAppointment(result) {
            if (this.$router.currentRoute.name === "appointment") {
                /**
                 * if search is triggered from Appointment page,
                 * schedule/index.vue will watch for appointment search
                 * and call applySearchAppointment()
                 */
                this.$store.commit("setAppointmentSearch", result);
            } else {
                this.$router.push({
                    name: "appointment",
                    params: {
                        view: "resourceTimeGridDay",
                        start: this._fromDateCarbon(result.date).toSeconds()
                    }
                });
            }

            this.$emit("result-click");
        }
    },
    beforeDestroy() {
        if (this.debouncedSearch && this.debouncedSearch.cancel) {
            this.debouncedSearch.cancel();
        }
    }
};
</script>

<style lang="scss" scoped>
$vet-text-subtlest: #626f86;
$vet-background-neutral-subtle-hovered: #091e420f;
$vet-border-focused: #388bff;

ul {
    list-style-type: none;
    padding: 0;
}

button.btn-option {
    display: flex;
    gap: 8px;
    width: 100%;
    padding: 6px 24px 4px;
    background: transparent;
    text-align: start;
    border: 0;

    &:focus-visible {
        outline: 2px solid $vet-border-focused;
        outline-offset: -2px;
    }

    &:hover {
        background-color: $vet-background-neutral-subtle-hovered;
    }

    .icon {
        width: 18px;
        height: 18px;
        margin-top: 1px;
        flex-shrink: 0;
    }

    .module {
        font-size: 12px;
        font-weight: 400;
        color: $vet-text-subtlest;
    }
}

.fw-medium {
    font-weight: 500;
}

.loading-wrapper {
    display: flex;
    justify-content: center;
    padding: 16px 20px 0 20px;
}

.no-data {
    padding: 8px 20px 0 20px;
}
</style>
